<template>
  <div
    aria-label="Détail du paiement livreur"
    role="main"
  >
    <!-- Header with navigation - hide when printing -->
    <div
      class="d-print-none mb-3"
      aria-label="En-tête et actions principales"
    >
      <b-card>
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h4
              class="mb-1"
              aria-label="Titre de la page"
            >
              <feather-icon
                icon="FileTextIcon"
                class="mr-2"
              />
              Détail du Paiement #{{ id }}
            </h4>
          </div>
          <div class="d-flex align-items-center">
            <b-button
              :to="{ name: 'payment-kpi', params: { id } }"
              variant="gradient-primary"
              class="mr-2"
              aria-label="Voir les statistiques et KPI du paiement"
            >
              <feather-icon
                icon="BarChart3Icon"
                size="16"
                class="mr-1"
              />
              Voir Statistiques & KPI
            </b-button>
            <b-button
              variant="outline-secondary"
              class="mr-2"
              aria-label="Imprimer le détail du paiement"
              @click="printPage"
            >
              <feather-icon
                icon="PrinterIcon"
                size="16"
                class="mr-1"
              />
              Imprimer
            </b-button>
            <b-button
              :to="{ name: 'payment-list' }"
              variant="outline-primary"
              aria-label="Retour à la liste des paiements"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                size="16"
                class="mr-1"
              />
              Retour à la liste
            </b-button>
          </div>
        </div>
      </b-card>
    </div>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="text-center py-5 d-print-none"
      aria-live="polite"
      aria-busy="true"
    >
      <b-spinner
        variant="primary"
        label="Chargement..."
        class="mb-3"
      />
      <p class="text-muted">
        Chargement des informations du paiement livreur...
      </p>
    </div>

    <!-- Error State -->
    <b-alert
      v-if="error"
      variant="danger"
      show
      class="mb-3 d-print-none"
      aria-live="assertive"
      aria-atomic="true"
    >
      <feather-icon
        icon="AlertCircleIcon"
        class="mr-2"
      />
      {{ error }}
    </b-alert>

    <!-- Main Content -->
    <div
      v-if="payment && !isLoading"
      aria-label="Contenu principal du paiement livreur"
    >
      <!-- Print Layout Section -->
      <section class="invoice-preview-wrapper">
        <b-row class="invoice-preview">
          <!-- Col: Left (Invoice Container) -->
          <b-col
            cols="12"
            xl="12"
            md="12"
          >
            <b-card
              no-body
              class="invoice-preview-card"
            >

              <!-- Enhanced Print Header -->
              <div
                class="d-flex flex-row align-items-start justify-content-between invoice-header-print"
                style="padding-top: 0; margin-top: 0;"
              >
                <div
                  class="logo-wrapper"
                  style="min-width:220px;"
                >
                  <img
                    src="/apple-touch-icon.png"
                    style="max-width:180px; max-height:60px; margin-bottom:0; display:block;"
                    :alt="$branding.logo.alt"
                  >
                </div>
                <div
                  class="ml-2"
                  style="flex:1;"
                >
                  <h1 style="color: black; font-size: 1.7rem; margin-bottom: 0.2rem; font-weight: bold;">
                    Paiement N°: {{ payment.id }}
                  </h1>
                  <div style="font-size: 1.1rem; color: #222;">
                    <strong>Date:</strong> <span>{{ formatDate(payment.created_at) }}</span>
                  </div>
                </div>
                <div
                  class="company-info-box ml-2"
                  style="border: 2px solid black; padding: 0.7rem 1.2rem; background: #fff; min-width: 270px;"
                >
                  <div style="font-size: 1.1rem; color: #222;">
                    <strong>{{ company.name || $branding.company.name }}</strong><br>
                    <span>{{ company.tagline || $branding.company.tagline }}</span><br>
                    <strong>Matricule fiscale :</strong> {{ company.tax_identification_number || 'N/A' }}<br>
                    <strong>Adresse :</strong> {{ company.address || 'N/A' }}<br>
                    <strong>Tel :</strong> {{ company.phone1 || 'N/A' }}<br>
                    <strong>Livreur :</strong> {{ payment.delivery_man ? payment.delivery_man.username : 'N/A' }}
                  </div>
                </div>
              </div>

              <!-- Removed duplicated old header/payment/company info section -->

              <!-- Delivered Orders Section -->
              <b-card-body
                v-if="deliveredOrdersTable.length > 0"
                class="invoice-padding pt-0"
              >
                <div class="d-flex justify-content-between">
                  <p class="font-weight-bold text-dark h5 mb-1">
                    <feather-icon
                      class="mr-1"
                      icon="FolderPlusIcon"
                      style="width: 24px;height: 24px;"
                      color="green"
                    />
                    <span style="color:green;font-size: 20px;">Colis livré {{ payment.delivred_orders_count }}</span>
                  </p>
                  <div>
                    <p class="font-weight-bold text-dark h5 mb-1">
                      <feather-icon
                        class="mr-1"
                        icon="CheckSquareIcon"
                        style="width: 20px;height: 20px;"
                        color="green"
                      />
                      <span style="color:green;font-size: 20px;">Prix de colis livrés : {{ (payment.delivred_price || 0).toFixed(3) }} DT</span>
                    </p>
                  </div>
                </div>
                <b-table-lite
                  class="ta"
                  responsive
                  :items="deliveredOrdersTable"
                  :fields="deliveredFields"
                >
                  <template #cell(code)="data">
                    <h5>{{ data.item.code }}</h5>
                    <span
                      v-if="data.item.is_an_exchange"
                      style="color:red ; font-weight: bold;"
                    >
                      Échange
                    </span>
                  </template>
                </b-table-lite>
              </b-card-body>

              <!-- Returned Orders Section -->
              <b-card-body
                v-if="returnedOrdersTable.length > 0"
                class="invoice-padding pt-0"
              >
                <div class="d-flex justify-content-between">
                  <p class="font-weight-bold text-dark h5 mb-1">
                    <feather-icon
                      icon="FolderMinusIcon"
                      style="width: 24px;height: 24px;"
                      class="mr-1"
                      color="red"
                    />
                    <span style="color:red;font-size: 20px;">Colis retour {{ payment.return_orders_count }}</span>
                  </p>
                  <p class="font-weight-bold text-dark h5 mb-1">
                    <feather-icon
                      class="mr-1"
                      icon="DollarSignIcon"
                      style="width: 20px;height: 20px;"
                      color="red"
                    />
                    <span style="color:red;font-size: 20px;">Prix de colis retour : {{ (payment.return_price || 0).toFixed(3) }} DT</span>
                  </p>
                </div>
                <b-table-lite
                  class="ta"
                  responsive
                  :items="returnedOrdersTable"
                  :fields="returnedFields"
                >
                  <template #cell(code)="data">
                    <h5>{{ data.item.code }}</h5>
                    <span
                      v-if="data.item.is_an_exchange"
                      style="color:red ; font-weight: bold;"
                    >
                      Échange
                    </span>
                  </template>
                  <template #cell(motif_retour)="data">
                    <div v-if="data.item.last_return_reason">
                      <strong class="d-block text-danger">
                        Motif : {{ data.item.last_return_reason.return_reason }}
                      </strong>
                      <small
                        v-if="data.item.last_return_reason.created_at"
                        class="text-muted d-block"
                      >
                        Date du motif : {{ formatDate(data.item.last_return_reason.created_at) }}
                      </small>
                      <small
                        v-if="data.item.last_return_reason.scheduled_date"
                        class="text-info d-block"
                      >
                        Date planifiée : {{ formatDate(data.item.last_return_reason.scheduled_date) }}
                      </small>
                      <small
                        v-if="data.item.last_return_reason.additional_note"
                        class="text-secondary d-block"
                      >
                        Note : {{ data.item.last_return_reason.additional_note }}
                      </small>
                    </div>
                    <span
                      v-else
                      class="text-muted"
                    >-</span>
                  </template>
                </b-table-lite>
              </b-card-body>

              <!-- Debrief Generated Exchange Orders Section -->
              <b-card-body
                v-if="debriefExchangeOrdersTable.length > 0"
                class="invoice-padding pt-0"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <p class="font-weight-bold text-dark h5 mb-1">
                    <feather-icon
                      icon="RefreshCwIcon"
                      style="width: 24px;height: 24px;"
                      class="mr-1"
                      color="orange"
                    />
                    <span style="color:orange;font-size: 20px;">Colis échange {{ debriefExchangeOrdersTable.length }}</span>
                  </p>
                </div>
                <b-table-lite
                  class="ta debrief-exchange-table"
                  responsive
                  :items="debriefExchangeOrdersTable"
                  :fields="debriefExchangeFields"
                >
                  <template #cell(generated_code)="data">
                    <h5>{{ data.item.generated_code }}</h5>
                    <span style="color:orange ; font-weight: bold;">Nouveau colis</span>
                  </template>
                  <template #cell(source_code)="data">
                    <h5>{{ data.item.source_code }}</h5>
                    <span style="color:red ; font-weight: bold;">Colis source</span>
                  </template>
                  <template #cell(exchange_voucher_id)="data">
                    <b-button
                      v-if="data.item.exchange_voucher_id"
                      variant="outline-warning"
                      size="sm"
                      class="d-print-none"
                      :to="{ name: 'bon-echange-show', params: { id: data.item.exchange_voucher_id } }"
                    >
                      Bon #{{ data.item.exchange_voucher_id }}
                    </b-button>
                    <span
                      v-else
                      class="text-muted"
                    >-</span>
                  </template>
                </b-table-lite>
              </b-card-body>

              <!-- Signature Section -->
              <b-card-body class="d-flex justify-content-between mt-5">
                <div class="mb-5 ml-1">
                  <h3 style="color: black; font-size: 25px">
                    Signature
                  </h3>
                </div>
                <div>
                  <h1 style="color: black; font-size: 25px" />
                </div>
              </b-card-body>

              <hr class="invoice-spacing">
            </b-card>
          </b-col>
        </b-row>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import {
  BCard, BCardBody, BRow, BCol, BButton, BSpinner, BAlert, BTableLite,
} from 'bootstrap-vue'
import storeAuth from '@/store/store'

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BAlert,
    BTableLite,
  },
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      payment: null,
      isLoading: false,
      error: null,
      company: {},
      deliveredOrdersTable: [],
      returnedOrdersTable: [],
      exchangeOrdersTable: [],
      debriefExchangeOrdersTable: [],

      // Table field definitions
      deliveredFields: [
        { key: 'code', label: 'BL' },
        { key: 'fournisseur', label: 'Fournisseur' },
        { key: 'gouvernorat', label: 'Gouvernorat' },
        { key: 'delegation', label: 'Délégation' },
        { key: 'tel', label: 'Téléphone' },
        { key: 'destinataire', label: 'Destinataire' },
        { key: 'prix', label: 'Prix (DT)' },
      ],

      returnedFields: [
        { key: 'code', label: 'BL' },
        { key: 'fournisseur', label: 'Fournisseur' },
        { key: 'gouvernorat', label: 'Gouvernorat' },
        { key: 'delegation', label: 'Délégation' },
        { key: 'tel', label: 'Téléphone' },
        { key: 'destinataire', label: 'Destinataire' },
        { key: 'prix', label: 'Prix (DT)' },
        { key: 'motif_retour', label: 'Motif de retour', tdClass: 'motif-retour-cell' },
      ],

      exchangeFields: [
        { key: 'code', label: 'BL' },
        { key: 'fournisseur', label: 'Fournisseur' },
        { key: 'gouvernorat', label: 'Gouvernorat' },
        { key: 'tel', label: 'Téléphone' },
        { key: 'destinataire', label: 'Destinataire' },
        { key: 'prix', label: 'Prix (DT)' },
      ],

      debriefExchangeFields: [
        { key: 'generated_code', label: 'Nouveau colis' },
        { key: 'source_code', label: 'Colis source' },
        { key: 'exchange_voucher_id', label: 'Bon échange' },
        { key: 'client', label: 'Client' },
        { key: 'governorate', label: 'Gouvernorat' },
        { key: 'delegation', label: 'Délégation' },
        { key: 'phone', label: 'Téléphone' },
      ],
    }
  },
  created() {
    this.company = storeAuth.state.company || {}
    this.fetchPayment()
  },
  methods: {
    async fetchPayment() {
      this.isLoading = true
      this.error = null

      try {
        const response = await axios.get(`/api/payments/detail/${this.id}/`)

        // Check if we have valid data
        if (response.data && response.data.id) {
          this.payment = response.data
          this.processOrderData()
        } else {
          throw new Error('Invalid payment data received')
        }
      } catch (error) {
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          if (error.response.status === 404) {
            this.error = 'Paiement non trouvé'
          } else if (error.response.status === 403) {
            this.error = 'Vous n\'avez pas les permissions pour consulter ce paiement'
          } else {
            this.error = `Erreur du serveur: ${error.response.status} - ${error.response.statusText}`
          }
        } else if (error.request) {
          // The request was made but no response was received
          this.error = 'Erreur de connexion au serveur'
        } else {
          // Something happened in setting up the request that triggered an Error
          this.error = error.message || 'Erreur lors du chargement des données du paiement'
        }
      } finally {
        this.isLoading = false
      }
    },

    processOrderData() {
      if (!this.payment) {
        return
      }

      try {
        const deliveredOrders = Array.isArray(this.payment.delivred_orders) ? this.payment.delivred_orders : []
        const returnedOrders = Array.isArray(this.payment.return_orders) ? this.payment.return_orders : []
        const exchangeOrders = Array.isArray(this.payment.exchange_orders) ? this.payment.exchange_orders : []
        const debriefExchangeOrders = Array.isArray(this.payment.debrief_exchange_orders) ? this.payment.debrief_exchange_orders : []

        this.deliveredOrdersTable = deliveredOrders.map((order, index) => this.formatPaymentOrderRow(order, index, {
          includeDeliveryPrice: true,
        }))

        this.returnedOrdersTable = returnedOrders.map((order, index) => ({
          ...this.formatPaymentOrderRow(order, index),
          last_return_reason: order.last_return_reason || null,
        }))

        this.debriefExchangeOrdersTable = debriefExchangeOrders.map(row => ({
          generated_code: row.generated_order_code || row.generated_order_id || 'N/A',
          source_code: row.source_order_code || row.source_order_id || 'N/A',
          exchange_voucher_id: row.exchange_voucher_id || null,
          client: row.client || 'N/A',
          governorate: row.governorate || 'N/A',
          delegation: row.delegation || 'N/A',
          phone: row.phone || 'N/A',
        }))

        this.exchangeOrdersTable = exchangeOrders.map((order, index) => this.formatPaymentOrderRow(order, index, {
          forceExchange: true,
        }))
      } catch (error) {
        this.error = 'Erreur lors du traitement des données des commandes'
      }
    },

    formatPaymentOrderRow(order, index, options = {}) {
      const row = {
        code: order.display_cross_tenant_barcode && order.cross_tenant_barcode ? order.cross_tenant_barcode : (order.id || `Unknown-${index}`),
        gouvernorat: order.governorate_name || order.governorate || 'N/A',
        delegation: order.delegation_name || order.delegation || 'N/A',
        fournisseur: this.getSupplierName(order),
        tel: order.customer_info?.phone1 || order.phone1 || 'N/A',
        destinataire: order.customer_info?.name || order.name || 'N/A',
        adresse: order.customer_info?.address || order.address || 'N/A',
        prix: (parseFloat(order.price_ttc) || 0).toFixed(3),
        is_an_exchange: options.forceExchange ? true : (order.is_an_exchange || false),
      }

      if (options.includeDeliveryPrice) {
        row.prix_de_livraison = (parseFloat(order.price_delivery) || 0).toFixed(3)
      }

      return row
    },

    getSupplierName(order) {
      if (order.supplier_name) {
        return order.supplier_name
      }
      if (order.supplier?.username) {
        return order.supplier.username
      }
      if (order.supplier?.company_name) {
        return order.supplier.company_name
      }
      return 'N/A'
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const [year, month, day] = dateString.split('T')[0].split('-')
      const time = dateString.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')} | ${time}`
    },

    printPage() {
      window.print()
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/pages/app-invoice.scss";

table,
td,
th {
  padding: 10px;
  text-align: left;
  border: 1px solid black;
}

table {
  width: 100%;
  border-collapse: collapse;
}

.contact {
  background-color: #EA2F2F;
  padding: 10px;
  border-radius: 20px;
}

.logo-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 0;
}

.invoice-header-print {
  margin-top: 0 !important;
  padding-top: 0 !important;
  margin-bottom: 1.2rem;
}

.company-info-box {
  background: #fff;
  border-radius: 8px;
}

.invoice-spacing {
  margin-top: 1.2rem;
  margin-bottom: 1.2rem;
}

.invoice-padding {
  padding: 1.5rem;
}

.motif-retour-cell {
  white-space: normal !important;
  word-break: break-word;
}
</style>

<style lang="scss">
@media print {
  @page {
    margin: 10mm;
    size: A4;
  }

  html, body {
    height: auto !important;
  }

  #app {
    margin: 0 !important;
    padding: 0 !important;
  }

  .invoice-preview-wrapper {
    margin: 0 !important;
    padding: 0 !important;
  }

  .invoice-preview-card {
    margin: 0 !important;
  }

  .invoice-header-print {
    margin-top: 0 !important;
    padding-top: 0 !important;
    margin-bottom: 0.5rem !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .invoice-preview-card .card-body {
    padding: 0 0 0.8rem 0 !important;
  }

  // Fix Bootstrap table-responsive clipping in print
  .table-responsive {
    overflow: visible !important;
    width: 100% !important;
  }

  .ta {
    overflow-x: visible !important;
    width: 100% !important;
  }

  .ta table {
    table-layout: fixed !important;
    width: 100% !important;
    border-collapse: collapse !important;
  }

  // Cell base: compact padding, small font, no overflow
  .ta td,
  .ta th {
    padding: 4px 5px !important;
    font-size: 10px !important;
    line-height: 1.3 !important;
    vertical-align: middle !important;
    word-break: break-word !important;
    overflow-wrap: break-word !important;
    white-space: normal !important;
  }

  // Flatten the h5 inside BL cell so it doesn't add height
  .ta td h5 {
    font-size: 10px !important;
    font-weight: bold !important;
    margin: 0 !important;
    line-height: 1.3 !important;
  }

  // Column widths — 7-column table (delivered / exchange)
  // col 1: BL, 2: Fournisseur, 3: Gouvernorat, 4: Délégation, 5: Téléphone, 6: Destinataire, 7: Prix
  .ta th:nth-child(1), .ta td:nth-child(1) { width: 12%; }  /* BL */
  .ta th:nth-child(2), .ta td:nth-child(2) { width: 14%; }  /* Fournisseur */
  .ta th:nth-child(3), .ta td:nth-child(3) { width: 10%; }  /* Gouvernorat */
  .ta th:nth-child(4), .ta td:nth-child(4) { width: 12%; }  /* Délégation */
  .ta th:nth-child(5), .ta td:nth-child(5) { width: 10%; }  /* Téléphone */
  .ta th:nth-child(6), .ta td:nth-child(6) { width: 13%; }  /* Destinataire */
  .ta th:nth-child(7), .ta td:nth-child(7) { width: 9%;  }  /* Prix */
  // col 8: Motif de retour (returned table only) gets remaining space
  .ta th:nth-child(8), .ta td:nth-child(8) { width: 20%; }  /* Motif */

  .debrief-exchange-table th:nth-child(3),
  .debrief-exchange-table td:nth-child(3) {
    display: none !important;
  }

  body {
    background-color: transparent !important;
  }

  nav.header-navbar {
    display: none;
  }

  .main-menu {
    display: none;
  }

  .header-navbar-shadow {
    display: none !important;
  }

  .content.app-content {
    margin-left: 0 !important;
    padding: 0 !important;
    margin-top: 0 !important;
  }

  .contact {
    background-color: #EA2F2F;
    padding: 20px;
    border-radius: 25px;
    -webkit-print-color-adjust: exact;
  }

  footer.footer {
    display: none;
  }

  .card {
    background-color: transparent;
    box-shadow: none;
  }

  .customizer-toggle {
    display: none !important;
  }

  .d-print-none {
    display: none !important;
  }

  // Invoice Specific Styles
  .invoice-preview-wrapper {
    .row.invoice-preview {
      .col-md-8 {
        max-width: 100%;
        flex-grow: 1;
      }
    }

    // Action Right Col
    .invoice-actions {
      display: none;
    }
  }

  table,
  td,
  th {
    text-align: left;
    border: 1px solid black;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  .table thead th {
    border: 2px solid black;
    background-color: #f8f9fa !important;
    -webkit-print-color-adjust: exact;
  }

  // Keep section headings readable
  h1, h2, h3, h4, h5 {
    color: black !important;
  }

  // Keep status colors for motif/section labels
  .text-danger {
    color: #c00 !important;
  }

  .text-info {
    color: #555 !important;
  }

  .text-muted {
    color: #666 !important;
  }

  // Motif column: allow full content, no max-width clipping
  .motif-retour-cell {
    max-width: none !important;
    white-space: normal !important;
    word-break: break-word !important;
    font-size: 11px !important;
  }
}
</style>
